import React from "react";
import CaseStudy from "../../components/caseStudy/CaseStudy";
import BPMFlowDemo from "../../assets/demo.mp4";
import BPMFlowDemo2 from "../../assets/nlp.mp4";
import { FaReact, FaJava, FaGitAlt } from "react-icons/fa";
import {
  SiTypescript, SiSpringboot, SiApachekafka, SiPostgresql, SiRedis,
  SiDocker, SiJenkins, SiGrafana, SiPrometheus,
} from "react-icons/si";
import { MdAutoAwesome } from "react-icons/md";

// Content only — the layout lives in components/caseStudy/CaseStudy.jsx.
const study = {
  slug: "bpmflow",
  title: "BPMFlow",
  subtitle:
    "A microservices-based BPM platform where business processes are modeled visually, executed by an embedded Camunda engine, and monitored in real time — built as an end-of-studies project at TEK-UP.",
  badges: [
    { label: "Business Process Management Platform" },
    { label: "Camunda Workflow Engine", icon: <MdAutoAwesome size={13} /> },
    { label: "Microservices Architecture" },
  ],
  tags: ["React", "TypeScript", "Spring Boot", "Camunda", "Kafka", "PostgreSQL", "Docker"],
  facts: [
    { label: "Type", value: "Business process management platform" },
    { label: "Engine", value: "Camunda 7, embedded" },
    { label: "Architecture", value: "Seven Spring Boot microservices behind an API Gateway" },
    { label: "Context", value: "End-of-studies project at TEK-UP" },
  ],
  media: [
    { type: "video", src: BPMFlowDemo, label: "Platform demo" },
    { type: "video", src: BPMFlowDemo2, label: "NLP demo" },
  ],

  overview: {
    title: "What is BPMFlow?",
    paragraphs: [
      "BPMFlow combines a visual BPMN/DMN/form modeler with a real Camunda-based execution engine, split across seven independently deployable Spring Boot microservices. A process gets designed as a diagram, decision tables handle the business rules behind it, and forms collect the data at each human step — then the same diagram becomes a live monitoring view once the process is running.",
      "Three realistic example processes ship with the project — an employee leave request, a multi-step purchase approval, and a real-estate financing workflow — each with its own BPMN diagram, DMN decision tables, and forms, to prove the platform against more than one shape of business process.",
    ],
  },
  problem: {
    paragraphs: [
      "Automating a business process — a leave request, a purchase approval, a loan underwriting flow — usually means either a developer wiring each one up by hand, or an expensive proprietary BPM suite bolted onto legacy infrastructure as a monolith. Neither scales cleanly, and neither makes it easy to see exactly where a specific running instance is stuck right now.",
      "BPMFlow explores building that capability as a properly decomposed platform instead: a real workflow engine, exposed through independently scalable services, with the running state of every process visible rather than buried in logs.",
    ],
  },
  solution: {
    steps: [
      "Client (React)",
      "API Gateway — JWT auth",
      "Eureka Service Discovery",
      "Target Microservice (Auth / User / BPM / Broker / Upload)",
      "PostgreSQL · Redis · Kafka",
    ],
    note:
      "Every request enters through a single Gateway that validates the JWT and looks up the target service via Eureka. Each microservice owns its own concern — the BPM service never has to know how notifications or file storage work internally.",
  },

  features: [
    {
      title: "Visual BPMN Modeler",
      description: "A drag-and-drop process designer (bpmn-js) with linting against Camunda-compatible rules before a process can be deployed.",
    },
    {
      title: "DMN Decision Tables",
      description: "Business rules — approval routing, risk scoring, auto-approval thresholds — are modeled as decision tables and invoked from the process, not hardcoded inside it.",
    },
    {
      title: "Dynamic Form Builder",
      description: "Forms are built visually and bound to process variables, rendered per user task without any custom frontend code.",
    },
    {
      title: "Live Process Execution",
      description: "Every deployed process runs on an embedded Camunda 7 engine — this is a real execution engine, not a diagram simulation.",
    },
    {
      title: "Real-Time Token Visualization",
      description: "The same diagram used to design a process highlights which activities are active or completed as a running instance moves through it.",
    },
    {
      title: "Kafka-Driven Notifications",
      description: "Task assignments and process events are published to Kafka and pushed live to the browser over Server-Sent Events — no polling.",
    },
    {
      title: "Role-Based Access Control",
      description: "A dedicated User Service manages users, roles and granular permissions, enforced centrally at the API Gateway.",
    },
    {
      title: "Independently Deployable Microservices",
      description: "Auth, User, BPM, Broker and File Storage each run as separate Spring Boot services behind a Gateway, registered with Eureka and configured through a central Config Server.",
    },
    {
      title: "Observability Built In",
      description: "Prometheus metrics and Grafana dashboards across services, plus a Kafka UI for inspecting the message broker directly.",
    },
    {
      title: "CI/CD Pipeline",
      description: "Jenkins builds and deploys both the backend microservices and the frontend.",
    },
  ],

  diagrams: [
    { label: "Request flow", steps: ["Client", "API Gateway (JWT)", "Eureka Server", "Target Microservice", "Config Server"] },
    {
      label: "Process execution flow",
      steps: [
        "Visual Modeler (React)",
        "Deploy",
        "BPM Service — Camunda Engine",
        "PostgreSQL (engine runtime + history)",
        "Kafka event → Broker Service → SSE → Live Notification",
      ],
    },
  ],

  challenges: [
    {
      title: "Decomposing a BPM engine into real microservices",
      problem: "A workflow engine like Camunda is normally embedded in a single application — splitting authentication, user management, notifications and file storage into independent services means the process engine can't assume direct access to that data anymore.",
      solution: "Every cross-service concern (who a task is assigned to, where a notification goes) is resolved through Eureka-registered service calls behind the API Gateway rather than a shared database, so each service stays independently deployable.",
    },
    {
      title: "Making process execution visible, not just logged",
      problem: "A BPMN engine tracks execution as rows in its own internal schema — not something a business user can read.",
      solution: "The frontend overlays live \"active\" and \"completed\" markers directly on the same BPMN diagram the user designed, driven by the engine's actual runtime state, so watching a process execute looks identical to designing it.",
    },
    {
      title: "Event-driven notifications instead of polling",
      problem: "Task assignments and process events happen inside the BPM service, but users are connected to the frontend, not to Camunda directly.",
      solution: "The BPM service publishes events to Kafka; a dedicated Broker service consumes them and pushes updates to connected clients over Server-Sent Events, so a new task in someone's queue doesn't depend on the frontend polling for it.",
    },
    {
      title: "Business rules separate from process flow",
      problem: "Hardcoding approval thresholds or routing logic inside the BPMN diagram turns every rule change into a redeployment.",
      solution: "Conditional logic — loan scoring, approval routing, auto-approval thresholds — lives in DMN decision tables invoked from Business Rule Tasks, so a rule can change independently of the process shape.",
    },
    {
      title: "Safely deleting engine state",
      problem: "A deployment's definition, running instances and history live in different Camunda table families (ACT_RE_*, ACT_RU_*, ACT_HI_*) — deleting the wrong scope leaves orphaned rows or breaks running processes.",
      solution: "Cleanup goes through Camunda's own RepositoryService cascade-delete API instead of manual SQL, after mapping out exactly what a deployment, an instance, and its history each own.",
    },
  ],

  stack: [
    { group: "Frontend", items: ["React 19", "TypeScript", "Vite", "TailwindCSS", "TanStack Query"] },
    { group: "BPM & Modeling", items: ["Camunda 7 (embedded engine)", "bpmn-js", "dmn-js", "@bpmn-io/form-js"] },
    { group: "Backend", items: ["Java", "Spring Boot", "Spring Cloud (Eureka, Config Server, Gateway)"] },
    { group: "Messaging & Real-Time", items: ["Apache Kafka", "Server-Sent Events"] },
    { group: "Database", items: ["PostgreSQL", "Redis"] },
    { group: "DevOps", items: ["Docker", "Jenkins CI/CD", "Prometheus", "Grafana"] },
  ],
  stackIcons: [
    [FaReact, "React"],
    [SiTypescript, "TypeScript"],
    [FaJava, "Java"],
    [SiSpringboot, "Spring Boot"],
    [SiApachekafka, "Kafka"],
    [SiPostgresql, "PostgreSQL"],
    [SiRedis, "Redis"],
    [SiDocker, "Docker"],
    [SiJenkins, "Jenkins"],
    [SiPrometheus, "Prometheus"],
    [SiGrafana, "Grafana"],
    [FaGitAlt, "Git"],
  ],
};

const BPMFlowCaseStudy = () => <CaseStudy study={study} />;

export default BPMFlowCaseStudy;
